/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";
import { IconX } from "@tabler/icons-react";
import { useForm } from "react-hook-form";
import { useOrderCreateMutation } from "../../../apis/orderApis";
import { useClientAllListData } from "../../../apis/clientApis";
import { populateSenderData } from "../../../utilities/location";
import toast from "react-hot-toast";
import { OrderFormData } from "../../../types/types";
import SenderSection from "./SenderSection";
import ReceiverSection from "./ReceiverSection";
import ShipmentSection from "./ShipmentSection";
import PreviewModal from "./PreviewModal";
import { useProfileData, useClientPermissions } from "../../../apis/profileApis";
import { useSelector } from "react-redux";
import { selectClientPermissions } from "../../../features/auth/authSlice";
import ShipmentItemSection from "./ShipmentItemSection";
import { createPortal } from "react-dom";
import ButtonStyled from "../../../components/common/ButtonStyled";

interface AddOrdersModalsProps {
  onClose: () => void;
  fetchData: () => void;
  isSidebar?: boolean;
}

const AddOrdersModal: React.FC<AddOrdersModalsProps> = ({
  onClose,
  fetchData,
  isSidebar = false,
}) => {
  const [step, setStep] = useState(1);
  const [showPreview, setShowPreview] = useState(false);
  const [useDifferentSender, setUseDifferentSender] = useState(false);
  const [useFMOrder, setUseFMOrder] = useState(false);
  const [isShipmentSubmitted, setIsShipmentSubmitted] = useState(false);

  const { mutate: createOrder, isPending: isRequestPending } =
    useOrderCreateMutation();
  const { data: clients, isPending: isClientFetched } = useClientAllListData();
  const { data: profile, isSuccess } = useProfileData();

  useClientPermissions();

  const permissions = useSelector(selectClientPermissions);

  const {
    register,
    setValue,
    clearErrors,
    setError,
    formState: { errors },
    watch,
    control,
    handleSubmit,
    trigger,
  } = useForm<OrderFormData>({
    mode: "onChange",
  });

  // Pre-select current profile client when loaded
  useEffect(() => {
    if (profile?.id && clients?.length) {
      handleClientSelect(profile.id);
    } else if (profile?.access) {
      const access = profile.access.toUpperCase();
      if (access === "FM") {
        setUseFMOrder(true);
      } else if (access === "LM") {
        setUseFMOrder(false);
      } else if (access === "BOTH") {
        setUseFMOrder(true);
      }
    }
  }, [profile, clients]);

  // Clear products when FM is disabled (since only FM orders have products)
  useEffect(() => {
    if (!useFMOrder) {
      setValue("products", []);
    }
  }, [useFMOrder, setValue]);

  // =========================
  // STEP VALIDATION
  // =========================

  const validateStep = async (): Promise<boolean> => {
    if (step === 1) {
      let senderValid = false;

      if (useDifferentSender) {
        senderValid = await trigger([
          "sender.name",
          "sender.mobile",
          "sender.email",
          "sender.country",
          "sender.city",
          "sender.district",
          "sender.zipCode",
          "sender.address",
          "sender.nationalAddress",
        ] as any);
      } else {
        senderValid = await trigger(["clientId"]);
      }

      const receiverValid = await trigger([
        "receiver.name",
        "receiver.mobile",
        "receiver.email",
        "receiver.country",
        "receiver.city",
        "receiver.district",
        "receiver.address",
        "receiver.zipCode",
        "receiver.latitude",
        "receiver.longitude",
      ] as any);

      return senderValid && receiverValid;
    }

    if (step === 2) {
      const shipmentValid = await trigger([
        "shipment.referenceNumber",
        "shipment.shipmentCost",
        "shipment.paymentType",
        "shipment.numberOfPieces",
        "shipment.totalWeight",
        "shipment.serviceType",
        "shipment.shipmentType",
        "shipment.description",
      ] as any);

      if (useFMOrder) {
        const products = watch("products") || [];
        const productFields = products.flatMap((_: any, i: number) => [
          `products.${i}.pId`,
          `products.${i}.quantity`,
        ]);

        const productValid = await trigger(productFields as any);
        return shipmentValid && productValid;
      }

      return shipmentValid;
    }

    return true;
  };

  // =========================
  // NEXT / BACK STEPS
  // =========================

  const handleNext = async () => {
    if (step === 2) {
      setIsShipmentSubmitted(true);
    }
    const isValid = await validateStep();

    if (!isValid) {
      toast.error("Please fill all required fields");
      return;
    }

    if (step < 2) {
      setStep((prev) => prev + 1);
    } else {
      setShowPreview(true);
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep((prev) => prev - 1);
    }
  };

  // =========================
  // CREATE ORDER
  // =========================

  const handleCreate = async (data: OrderFormData) => {
    const resolvedNationalAddress =
      watch("receiver.shortAddress") || watch("receiver.nationalAddress") || "";
    const resolvedSenderNationalAddress =
      watch("sender.shortAddress") || watch("sender.nationalAddress") || "";

    const updatedData = {
      ...data,
      clientId: data.clientId || profile?.id,
      type: "LM",
      sender: useDifferentSender
        ? {
            ...data.sender,
            nationalAddress: resolvedSenderNationalAddress,
            shortAddress: resolvedSenderNationalAddress,
          }
        : data.sender,
      receiver: {
        ...data.receiver,
        address:
          watch("receiver.address") || watch("receiver.streetName") || "",
        nationalAddress: resolvedNationalAddress,
        shortAddress: resolvedNationalAddress,
      },
    };

    createOrder(updatedData, {
      onSuccess: () => {
        setShowPreview(false);
        fetchData();
        onClose();
        toast.success("Order created successfully");
      },
      onError: (error: any) => {
        toast.error(error?.response?.data?.message || "Failed to create order");
      },
    });
  };

  // =========================
  // CLIENT SELECT
  // =========================

  const handleClientSelect = async (clientId: string) => {
    const sender = clients?.find((client: any) => client?.id === clientId);

    if (!sender) return;

    setValue("clientId", clientId);
    setValue("clientName", sender?.name);
    setValue("sender.name", sender?.name);
    setValue("sender.mobile", sender?.mobileNo);
    setValue("sender.email", sender?.email);
    setValue("sender.country", sender?.country);
    setValue("sender.city", sender?.city);
    setValue("sender.district", sender?.district);
    setValue("sender.countryName", sender?.countryName);
    setValue("sender.cityName", sender?.cityName);
    setValue("sender.districtName", sender?.districtName);
    setValue("sender.address", sender?.address);
    setValue("sender.nationalAddress", sender?.shortAddress);
    setValue("sender.dialCode", sender?.dialCode);

    if (!sender?.latitude || !sender?.longitude || !sender?.zipCode) {
      try {
        const geocoded = await populateSenderData(sender);
        setValue("sender.zipCode", geocoded?.zipCode || "");
        setValue("sender.latitude", geocoded?.latitude || "");
        setValue("sender.longitude", geocoded?.longitude || "");
      } catch (error) {
        console.error(error);
        setValue("sender.zipCode", "");
        setValue("sender.latitude", "");
        setValue("sender.longitude", "");
      }
    } else {
      setValue("sender.zipCode", sender?.zipCode || "");
      setValue("sender.latitude", sender?.latitude || "");
      setValue("sender.longitude", sender?.longitude || "");
    }

    if (sender?.access === "FM") {
      setUseFMOrder(true);
    } else if (sender?.access === "LM") {
      setUseFMOrder(false);
    } else if (sender?.access === "BOTH") {
      setUseFMOrder(true);
    }
  };

  const selectedAccess = (clients?.find((c: any) => c.id === watch("clientId"))?.access || profile?.access)?.toUpperCase();
  const isBothAccess = selectedAccess === "BOTH";

  const content = (
    <div className={`flex flex-col h-full bg-transparent ${isSidebar ? "" : "rounded-3xl border border-gray-200 bg-white shadow-sm overflow-hidden"}`}>
      {/* HEADER */}
      {!isSidebar && (
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-200 bg-white shrink-0">
          <h2 className="text-lg font-bold text-[#212121]">Create Order</h2>

          <div className="flex items-center gap-2">
            {/* FM toggle chip */}
            {isBothAccess && (
              <button
                type="button"
                onClick={() => setUseFMOrder(!useFMOrder)}
                className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-bold transition-all ${
                  useFMOrder
                    ? "bg-[#FBD037] border-[#FBD037] text-[#212121]"
                    : "bg-white border-gray-300 text-gray-500 hover:border-[#FBD037]"
                }`}
              >
                <span className={`w-1.5 h-1.5 rounded-full ${useFMOrder ? "bg-[#212121]" : "bg-gray-400"}`} />
                FM
              </button>
            )}

            <ButtonStyled
              type="button"
              variant="outline"
              onClick={showPreview ? () => setShowPreview(false) : (step === 1 ? onClose : handleBack)}
              value={showPreview ? "Back" : (step === 1 ? "Cancel" : "Back")}
            />

            <ButtonStyled
              type="button"
              onClick={showPreview ? handleSubmit(handleCreate) : handleNext}
              disabled={isRequestPending}
              isPending={isRequestPending}
              variant="solid"
              value={showPreview ? "Submit" : (step === 2 ? "Preview" : "Next")}
            />

            <button
              type="button"
              onClick={onClose}
              className="p-1.5 rounded-lg border border-[#D2D2D2] bg-white hover:bg-gray-100 transition-colors text-slate-700"
              title="Close"
            >
              <IconX size={18} />
            </button>
          </div>
        </div>
      )}

      {/* BODY */}
      <div className="w-full flex-1 overflow-y-auto min-h-0">
        <div className="h-full overflow-y-auto space-y-4 p-4 scrollbar-clean">
          {/* STEPPER */}
          {!showPreview && !isSidebar && (
            <div className="flex items-center justify-center gap-4 py-4 px-2">
              {[1, 2].map((item) => (
                <div
                  key={item}
                  className={`w-1/2 border-b-4 rounded-t-md ${
                    step >= item
                      ? "border-[#FBD037] text-black"
                      : "border-gray-200 text-gray-500"
                  }`}
                />
              ))}
            </div>
          )}

          {/* STEP 1 */}
          {step === 1 && !showPreview && (
            <div className="space-y-3">
              {!isSidebar && (
                <SenderSection
                  clients={clients || []}
                  register={register}
                  errors={errors}
                  setValue={setValue}
                  clearErrors={clearErrors}
                  watch={watch}
                  control={control}
                  useDifferentSender={useDifferentSender}
                  setUseDifferentSender={setUseDifferentSender}
                  useFMOrder={useFMOrder}
                  setUseFMOrder={setUseFMOrder}
                  onClientSelect={handleClientSelect}
                  isSidebar={isSidebar}
                />
              )}
              <ReceiverSection
                register={register}
                errors={errors}
                setValue={setValue}
                watch={watch}
                control={control}
                clearErrors={clearErrors}
                setError={setError}
                isSidebar={isSidebar}
              />
            </div>
          )}

          {/* STEP 2 */}
          {step === 2 && !showPreview && (
            <div className="space-y-3">
              <ShipmentSection
                register={register}
                errors={errors}
                setValue={setValue}
                watch={watch}
                clearErrors={clearErrors}
                isSidebar={isSidebar}
                isSubmitted={isShipmentSubmitted}
                disableWeight={useFMOrder}
                useFMOrder={useFMOrder}
                setUseFMOrder={setUseFMOrder}
                isBothAccess={isBothAccess}
              />
              {useFMOrder && (permissions?.length ? permissions?.some((p: any) => p?.module === "products") : true) && (
                <ShipmentItemSection
                  setValue={setValue}
                  watch={watch}
                  register={register}
                  errors={errors}
                  control={control}
                  isSidebar={isSidebar}
                />
              )}
            </div>
          )}

          {/* PREVIEW (inline when in sidebar mode) */}
          {showPreview && isSidebar && (
            <PreviewModal
              onClose={onClose}
              onBack={() => setShowPreview(false)}
              watch={watch}
              onSubmit={handleSubmit(handleCreate)}
              isSubmitting={isRequestPending}
              isSidebar={isSidebar}
            />
          )}

          {/* Footer buttons inside the form */}
          {isSidebar && (
            <div className="flex items-center justify-end gap-2 pt-2">
              <ButtonStyled
                type="button"
                variant="outline"
                onClick={showPreview ? () => setShowPreview(false) : (step === 1 ? onClose : handleBack)}
                value={showPreview ? "Back" : (step === 1 ? "Cancel" : "Back")}
              />

              <ButtonStyled
                type="button"
                onClick={showPreview ? handleSubmit(handleCreate) : handleNext}
                disabled={isRequestPending}
                isPending={isRequestPending}
                variant="solid"
                value={showPreview ? "Submit" : (step === 2 ? "Preview" : "Next")}
              />
            </div>
          )}
        </div>
      </div>

      {/* PREVIEW MODAL (dialog mode) */}
      {showPreview && !isSidebar && (
        <PreviewModal
          onClose={onClose}
          onBack={() => setShowPreview(false)}
          watch={watch}
          onSubmit={handleSubmit(handleCreate)}
          isSubmitting={isRequestPending}
          isSidebar={isSidebar}
        />
      )}
    </div>
  );

  if (isSidebar) {
    return content;
  }

  return createPortal(
    <div className="fixed top-0 left-0 w-full h-screen bg-black/60 z-50 flex items-center justify-center p-4">
      <div className="rounded-2xl xl:w-7/12 lg:w-7/12 w-11/12 h-[calc(100vh-2rem)] relative overflow-hidden">
        {content}
      </div>
    </div>,
    document.body
  );
};

export default AddOrdersModal;
